// جلسهٔ درمان — the visit workspace's data layer.
//
// One hook per concern, all in this file because they share one rule: EVERY ONE
// OF THEM IS SCOPED TO THE VISIT the header resolved, never to the id in the
// URL. `useVisit` reads the appointment and hands back the patient id the
// SERVER says it belongs to; every other hook takes that id. A screen that
// reads the patient from the path and the visit from the id can be made to
// paint one patient's chart under another's name by editing the address bar,
// and the BFF's double scope check would not stop it — both ids would be the
// caller's own.
//
// Nothing here polls. امروز polls because the desk changes it underneath the
// dentist; a visit is changed by the person looking at it, so a refetch happens
// after a write and not on a timer.

import React from 'react';
import { bffQuery, bffMutation, errorCode } from '../lib/bffClient.js';
import {
  VISIT_QUERY, VISIT_NOTE_QUERY, SAVE_VISIT_NOTE, PRIOR_VISITS_QUERY,
  TREATMENT_PLANS_QUERY, TREATMENT_CATALOG_QUERY,
  CREATE_TREATMENT_PLAN, UPDATE_TREATMENT_PLAN, SET_PLAN_ITEM_STATUS,
  APPROVE_TREATMENT_PLAN, CREATE_CLINIC_SERVICE,
  TREATMENT_RECORDS_QUERY, RECORD_TREATMENT, VOID_TREATMENT_RECORD,
  COMPLETE_VISIT,
  VISIT_ORDERS_QUERY, ORDER_CATALOG_QUERY, CREATE_VISIT_ORDER, SET_ORDER_STATUS,
  VISIT_DIAGNOSES_QUERY, VISIT_DIFFERENTIAL_QUERY, RECORD_DIAGNOSIS, DELETE_DIAGNOSIS,
  VISIT_QUESTIONS_QUERY, ASK_VISIT_QUESTION,
  REFINE_VISIT_NOTE, GENERATE_VISIT_ADVICE, EXTRACT_TOOTH_FINDINGS,
  VISIT_FILES_QUERY, UPLOAD_VISIT_FILE, DELETE_VISIT_FILE,
  RUN_FILE_OCR, ANALYZE_VISIT_IMAGE, SAVE_IMAGE_ANNOTATIONS, REGISTER_VISIT_IMAGE,
  VISIT_MEDICATIONS_QUERY, MEDICATION_SUGGESTIONS_QUERY, VISIT_ORDER_SUGGESTIONS_QUERY,
  activePlan,
} from '../screens/Visit/Visit.shared.js';

/**
 * A load that cannot be overtaken by a slower one for a different subject.
 *
 * Every hook here can be re-pointed while a request is in flight — the dentist
 * moves from one visit to the next — and a stale response painting over the
 * current one is the failure this exists to prevent. The pattern is the same
 * one useDentalChart uses; it is repeated rather than abstracted because the
 * "asked" key differs per hook and an abstraction over it hid the bug once.
 */
function useGuardedLoad(key) {
  const wanted = React.useRef(key);
  wanted.current = key;
  return wanted;
}

/**
 * The state every «پیشنهاد هوش مصنوعی» button on this screen shares.
 *
 * Four of them now (تشخیص, پرسش‌ها, دستورات, دارو) and they all obey the same
 * three rules, so the rules live here once rather than four times:
 *
 *  1. NOTHING LOADS BY ITSELF. Every one of these calls bills a credit, so the
 *     pane opens with a button, never with a bill.
 *  2. The CREDIT case gets its own branch. «شارژ کنید» is actionable and
 *     «در دسترس نیست» is not — the same split نسخه makes for its sweep.
 *  3. `data === null` means NOT ASKED YET, which is a different thing from an
 *     empty answer. Rendering «پیشنهادی نیست» for a button nobody pressed is
 *     the bug this distinction prevents.
 */
function useAiSuggestions(label, run) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const ask = React.useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      setData(await run(...args));
      return null;
    } catch (err) {
      console.error(`[${label}] failed:`, err?.message);
      setError(errorCode(err) === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable');
      return err;
    } finally {
      setLoading(false);
    }
  }, [label, run]);

  // Dismissed or acted on, a suggestion set leaves the screen the same way.
  const clear = React.useCallback(() => { setData(null); setError(null); }, []);

  return { data, loading, error, ask, clear };
}

/**
 * THE VISIT. The appointment, its patient, and whether it may still be written.
 *
 * `patientId` from here is the one every other hook must be given.
 */
export function useVisit(appointmentId) {
  const [visit, setVisit] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);
  const [completing, setCompleting] = React.useState(false);
  const wanted = useGuardedLoad(appointmentId);

  React.useEffect(() => {
    if (!appointmentId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    const asked = appointmentId;
    setLoading(true);
    bffQuery(VISIT_QUERY, { appointmentId: asked })
      .then((data) => {
        if (!alive || wanted.current !== asked) return;
        // null is NOT_FOUND — or a walk-in with no patient record. The screen
        // renders that as its own state; it is not an error and offers no retry.
        setVisit(data?.visit || null);
        setError(null);
      })
      .catch((err) => {
        if (!alive || wanted.current !== asked) return;
        console.error('[useVisit] failed:', err?.message);
        setError(err);
      })
      .finally(() => {
        if (alive && wanted.current === asked) setLoading(false);
      });
    return () => { alive = false; };
  }, [appointmentId, nonce, wanted]);

  /**
   * What the last close did about «نوبت بعدی» — raised | refreshed |
   * nothing_outstanding | declined | already_booked | unavailable.
   *
   * A REF, not state, and deliberately: the screen reads it in the same tick it
   * awaits `complete`, and a state update would not have flushed by then. It is
   * also not part of `visit` — it is what one close DID, not a property of the
   * appointment, and a copy stored on the row would age the moment the plan moved.
   */
  const followUpOutcome = React.useRef(null);

  /**
   * «پایان جلسه» — close the visit.
   *
   * Resolves to null on success or the Error on failure, the shape every write
   * on this screen uses. The caller flushes unsaved work first and skips this
   * entirely when that flush failed: `completed` is terminal for this BFF, so a
   * close that lands before the note does loses the note for good.
   *
   * `followUp` is what the dentist answered about the patient's next visit, and
   * the outcome lands on `followUpOutcome.current`.
   */
  const complete = React.useCallback(async (patientId, followUp = null) => {
    setCompleting(true);
    try {
      // `followUp` is what the dentist answered about «نوبت بعدی». Omitted
      // entirely when null — saying nothing is NOT «لازم نیست», and the service
      // still tells the desk, with its default interval.
      const data = await bffMutation(COMPLETE_VISIT, {
        patientId, appointmentId, ...(followUp ? { followUp } : {}),
      });
      // Patch rather than refetch: the mutation already answered with the row
      // it wrote, and a reload here would race the desk touching the same
      // appointment. `reload` stays available for the ordinary path.
      setVisit((v) => (v ? { ...v, ...(data?.completeVisit || {}) } : v));
      followUpOutcome.current = data?.completeVisit?.followUpOutcome || null;
      return null;
    } catch (err) {
      console.error('[useVisit] complete failed:', err?.message);
      return err;
    } finally {
      setCompleting(false);
    }
  }, [appointmentId]);

  return {
    visit, loading, error, completing, complete, followUpOutcome,
    reload: () => setNonce((n) => n + 1),
  };
}

/**
 * The SOAP subjective for this visit.
 *
 * `save` resolves to null on success or the Error on failure, so the caller can
 * keep what the dentist typed on screen and say why it did not land — the
 * shape useDentalChart's `record` uses, for the same reason.
 */
export function useVisitNote({ patientId, appointmentId }) {
  const [note, setNote] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  /**
   * Bumped on every successful save.
   *
   * The note's `id` does NOT change when it is revised — the row is updated in
   * place — so a form that re-syncs on `note.id` never notices a write made
   * somewhere else. That is a real loop on this screen: answering a question in
   * پرسش‌ها appends a finding to the same note the subjective form is bound to.
   * Without this the dentist never saw the appended finding, and their next
   * «ذخیره» wrote the stale text back over it.
   */
  const [revision, setRevision] = React.useState(0);
  const wanted = useGuardedLoad(appointmentId);

  const load = React.useCallback(async ({ initial } = {}) => {
    if (!patientId || !appointmentId) {
      setLoading(false);
      return;
    }
    const asked = appointmentId;
    if (initial) setLoading(true);
    try {
      const data = await bffQuery(VISIT_NOTE_QUERY, { patientId, appointmentId: asked });
      if (wanted.current !== asked) return;
      setNote(data?.visitNote || null);
      setError(null);
    } catch (err) {
      if (wanted.current !== asked) return;
      console.error('[useVisitNote] failed:', err?.message);
      if (initial) setError(err);
    } finally {
      if (initial && wanted.current === asked) setLoading(false);
    }
  }, [patientId, appointmentId, wanted]);

  React.useEffect(() => { load({ initial: true }); }, [load]);

  const save = React.useCallback(async (patch) => {
    setSaving(true);
    try {
      const data = await bffMutation(SAVE_VISIT_NOTE, { patientId, appointmentId, ...patch });
      setNote(data?.saveVisitNote || null);
      setRevision((n) => n + 1);
      return null;
    } catch (err) {
      console.error('[useVisitNote] save failed:', err?.message);
      return err;
    } finally {
      setSaving(false);
    }
  }, [patientId, appointmentId]);

  return { note, revision, loading, error, saving, save, reload: () => load({ initial: true }) };
}

/** §3.1.2's "contextual linking to previous visits". Supplementary: it never errors the screen. */
export function usePriorVisits({ patientId, appointmentId }) {
  const [visits, setVisits] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const wanted = useGuardedLoad(appointmentId);

  React.useEffect(() => {
    if (!patientId || !appointmentId) {
      setLoading(false);
      return undefined;
    }
    let alive = true;
    const asked = appointmentId;
    setLoading(true);
    bffQuery(PRIOR_VISITS_QUERY, { patientId, appointmentId: asked, limit: 10 })
      .then((data) => {
        if (!alive || wanted.current !== asked) return;
        setVisits(data?.priorVisits || []);
      })
      .catch((err) => {
        // Swallowed on purpose: a missing history strip must not take the
        // subjective down with it.
        if (alive) console.error('[usePriorVisits] failed:', err?.message);
      })
      .finally(() => { if (alive && wanted.current === asked) setLoading(false); });
    return () => { alive = false; };
  }, [patientId, appointmentId, wanted]);

  return { visits, loading };
}

/**
 * طرح درمان for this patient, and the writes §3.1.3 asks for.
 *
 * Refetched whole after every write, never patched locally: the service derives
 * the totals and the item ids are REISSUED on a replace, so a local patch would
 * hold ids that no longer exist.
 */
export function useTreatmentPlans({ patientId, clinicId, appointmentId }) {
  const [plans, setPlans] = React.useState([]);
  // WHAT WAS ACTUALLY DONE — the other half of this screen, and the half the
  // plan cannot answer. A quote says what was agreed; only a record says it
  // happened, who did it, on which surfaces and with what. They load together
  // because «جلسهٔ ۲ از ۳» on a plan line is a fact about the records, so a
  // screen holding one without the other would draw a session count it cannot
  // justify.
  const [records, setRecords] = React.useState([]);
  // What a plan LINE may name — procedures, surgeries and follow-ups. NOT the
  // order catalogue: an order is work sent elsewhere, a treatment is work the
  // dentist does, and quoting a radiograph as a treatment is a quote for the
  // wrong thing on a document the patient signs.
  const [catalog, setCatalog] = React.useState([]);
  // A catalogue that could not be READ is not a clinic with no services, and
  // the plan section says two different things about them: one sends the
  // dentist to «کالا و خدمات» to define twenty services, and doing that during
  // an accounting outage sends them to redefine twenty they already have.
  const [catalogError, setCatalogError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const wanted = useGuardedLoad(patientId);

  const load = React.useCallback(async ({ initial } = {}) => {
    if (!patientId) {
      setLoading(false);
      return;
    }
    const asked = patientId;
    if (initial) setLoading(true);
    // The plans are the record and the catalogue is reference data. Settled
    // separately: a catalogue that fails to load still leaves the dentist able
    // to READ the plan and move its lines along.
    const [plansRes, catalogRes, recordsRes] = await Promise.allSettled([
      bffQuery(TREATMENT_PLANS_QUERY, { patientId, clinicId: clinicId || null }),
      bffQuery(TREATMENT_CATALOG_QUERY, { clinicId: clinicId || null }),
      bffQuery(TREATMENT_RECORDS_QUERY, { patientId, clinicId: clinicId || null }),
    ]);
    if (wanted.current !== asked) return;
    if (plansRes.status === 'fulfilled') {
      setPlans(plansRes.value?.treatmentPlans || []);
      setError(null);
    } else {
      console.error('[useTreatmentPlans] failed:', plansRes.reason?.message);
      if (initial) setError(plansRes.reason);
    }
    if (catalogRes.status === 'fulfilled') {
      setCatalog(catalogRes.value?.dentalTreatmentCatalog || []);
      setCatalogError(null);
    } else {
      console.error('[useTreatmentPlans] catalogue failed:', catalogRes.reason?.message);
      setCatalog([]);
      // THREE different things, and the screen says three different things.
      // CLINIC_REQUIRED means this visit names no clinic (or the dentist works
      // at several and none was chosen) — retrying will never fix it, and
      // telling them to «try again» is worse than telling them nothing.
      setCatalogError({
        error: catalogRes.reason,
        kind: errorCode(catalogRes.reason) === 'CLINIC_REQUIRED' ? 'noClinic' : 'unavailable',
      });
    }
    if (recordsRes.status === 'fulfilled') {
      setRecords(recordsRes.value?.treatmentRecords || []);
    } else {
      // Settled apart from the plans on purpose. A records read that fails must
      // not blank the plan: the dentist can still see and move the quote, and
      // the session counts fall back to the line's own status — which is
      // exactly what they were before records existed.
      console.error('[useTreatmentPlans] records failed:', recordsRes.reason?.message);
    }
    if (initial) setLoading(false);
  }, [patientId, clinicId, wanted]);

  React.useEffect(() => { load({ initial: true }); }, [load]);

  const run = React.useCallback(async (doc, variables) => {
    setSaving(true);
    try {
      await bffMutation(doc, variables);
      await load();
      return null;
    } catch (err) {
      console.error('[useTreatmentPlans] write failed:', err?.message);
      return err;
    } finally {
      setSaving(false);
    }
  }, [load]);

  const plan = activePlan(plans);

  /**
   * Add one line.
   *
   * The service has no per-item RPC — items are REPLACED as a set — so adding
   * means sending the list we want. The existing items are re-sent by value and
   * not by id for exactly the reason typeDefs/plan.js gives: ids are reissued on
   * replace, so a patch keyed on a stale read removes the wrong line.
   */
  const addItem = React.useCallback((item) => {
    const asInput = (i) => ({
      // Sent back for every EXISTING line, which is what keeps its id across
      // the replace. A treatment recorded against a line would otherwise be
      // orphaned by adding a second line to the plan.
      id: i.id || null,
      phase: i.phase || 'phase1',
      catalogId: i.catalogId,
      toothFdi: i.toothFdi || null,
      surfaces: i.surfaces || null,
      description: i.description || null,
      status: i.status || 'proposed',
      sortOrder: i.sortOrder ?? 0,
      quantity: i.quantity || 1,
    });
    // No open plan, or the open one is SIGNED — either way the line starts a
    // new course. A signed plan is what the patient agreed to and its lines
    // cannot change (the price lock), so appending to it would be refused by
    // the service; the dentist needs somewhere for the new treatment to go, and
    // a second course is what actually happens in a clinic.
    if (!plan || plan.approvedAtMs) {
      return run(CREATE_TREATMENT_PLAN, {
        patientId,
        clinicId: clinicId || null,
        title: null,
        items: [asInput({ ...item, sortOrder: 0 })],
      });
    }
    const items = [...(plan.items || []).map(asInput), asInput({ ...item, sortOrder: (plan.items || []).length })];
    return run(UPDATE_TREATMENT_PLAN, { planId: plan.id, items });
  }, [plan, patientId, clinicId, run]);

  const removeItem = React.useCallback((itemId) => {
    if (!plan) return Promise.resolve(null);
    const items = (plan.items || [])
      .filter((i) => i.id !== itemId)
      .map((i, index) => ({
        id: i.id, phase: i.phase, catalogId: i.catalogId, toothFdi: i.toothFdi || null,
        surfaces: i.surfaces || null, description: i.description || null,
        status: i.status, sortOrder: index, quantity: i.quantity || 1,
      }));
    return run(UPDATE_TREATMENT_PLAN, { planId: plan.id, items });
  }, [plan, run]);

  const setItemStatus = React.useCallback(
    (itemId, status) => (plan
      ? run(SET_PLAN_ITEM_STATUS, { planId: plan.id, itemId, status })
      : Promise.resolve(null)),
    [plan, run],
  );

  /**
   * THE LOCK — «تأیید پلن».
   *
   * Irreversible, and the one call on this screen that is. The amounts are NOT
   * sent: the BFF reads them from the clinic's own declarations at the moment
   * of signing, so a browser cannot name the number that goes onto a document
   * a patient signs.
   */
  const approve = React.useCallback(
    ({ approvedByPatient = true } = {}) => (plan
      ? run(APPROVE_TREATMENT_PLAN, { planId: plan.id, approvedByPatient })
      : Promise.resolve(null)),
    [plan, run],
  );

  /**
   * «خدمت جدید» — define a service for the CLINIC, from the chair (D3).
   *
   * The new entry is pushed onto the local catalogue immediately rather than
   * waited for on a refetch: the dentist pressed this because the list did not
   * have what they wanted, and making them hunt for it again afterwards is the
   * same problem twice. `load()` still runs, so the authoritative list replaces
   * this one a moment later.
   *
   * Returns { entry } on success and { error } on failure, rather than the bare
   * error the other writes return, because the caller needs the new service's
   * id to select it.
   */
  const createService = React.useCallback(async (input) => {
    setSaving(true);
    try {
      const data = await bffMutation(CREATE_CLINIC_SERVICE, {
        clinicId: clinicId || null,
        input,
      });
      const entry = data?.createClinicService;
      if (entry) setCatalog((prev) => [...prev, entry]);
      load();
      return { entry };
    } catch (err) {
      console.error('[useTreatmentPlans] createClinicService failed:', err?.message);
      return { error: err };
    } finally {
      setSaving(false);
    }
  }, [clinicId, load]);

  /**
   * «ثبت انجام» — the work, not the quote.
   *
   * ONE clinical act: the record, the chart change it caused and the plan line
   * it finished land together or not at all, in the service. Billing and stock
   * are separate writes made after it, so a failure of either cannot lose the
   * clinical fact — `billingState` / `stockState` say how far they got.
   *
   * `load()` afterwards rather than pushing the row in locally: recording may
   * also have moved the plan line to «انجام‌شده» and written to the chart, and
   * a local insert would show the record beside a line that still says
   * پذیرفته‌شده until something else happened to refetch.
   */
  const recordTreatment = React.useCallback(async (input) => {
    setSaving(true);
    try {
      const data = await bffMutation(RECORD_TREATMENT, {
        patientId,
        clinicId: clinicId || null,
        input: { ...input, appointmentId: input.appointmentId || appointmentId },
      });
      await load();
      return { record: data?.recordTreatment };
    } catch (err) {
      console.error('[useTreatmentPlans] recordTreatment failed:', err?.message);
      return { error: err };
    } finally {
      setSaving(false);
    }
  }, [patientId, clinicId, appointmentId, load]);

  /**
   * «ابطال» — strike it out. The row stays, with its reason.
   *
   * Resolves `{ error, chargeReversed }`. A billed treatment's charge is taken
   * back by the BFF through the order's session
   * (docs/TREATMENT_PROCESS_AND_SERVICE_BUNDLES_PLAN.md §5.4); `chargeReversed`
   * false means it could NOT be and still stands, null that there was none.
   */
  const voidRecord = React.useCallback(
    // `voidChartEvents` strikes out the chart change this treatment made, in the
    // same transaction. The dialog asks; it is never assumed here, because a
    // void of a BILLING duplicate leaves a tooth that really is crowned.
    async (recordId, reason, voidChartEvents = false) => {
      setSaving(true);
      try {
        const data = await bffMutation(
          VOID_TREATMENT_RECORD, { patientId, recordId, reason, voidChartEvents },
        );
        await load();
        return { error: null, chargeReversed: data?.voidTreatmentRecord?.chargeReversed ?? null };
      } catch (err) {
        console.error('[useTreatmentPlans] void failed:', err?.message);
        return { error: err, chargeReversed: null };
      } finally {
        setSaving(false);
      }
    },
    [patientId, load],
  );

  return {
    plans, plan, catalog, catalogError, loading, error, saving, records,
    addItem, removeItem, setItemStatus, approve, createService,
    recordTreatment, voidRecord,
    reload: () => load({ initial: true }),
  };
}

/**
 * دارو — what has been prescribed IN THIS VISIT.
 *
 * From the SERVER, not from local state. The panes are conditionally mounted,
 * so a list kept in the pane's own state is blanked by switching to دستورات and
 * back — while the rows exist server-side. It also gives the saved rows their
 * REAL ids, which is what anything later (an edit, a deletion) has to address.
 */
export function useVisitMedications({ patientId, appointmentId }) {
  const [medications, setMedications] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const wanted = useGuardedLoad(appointmentId);

  const load = React.useCallback(async ({ initial } = {}) => {
    if (!patientId || !appointmentId) {
      setLoading(false);
      return;
    }
    const asked = appointmentId;
    if (initial) setLoading(true);
    try {
      const data = await bffQuery(VISIT_MEDICATIONS_QUERY, { patientId, appointmentId: asked });
      if (wanted.current !== asked) return;
      setMedications(data?.listMedications || []);
    } catch (err) {
      // Supplementary: the prescribing form still works without the list of
      // what was already written.
      console.error('[useVisitMedications] failed:', err?.message);
    } finally {
      if (initial && wanted.current === asked) setLoading(false);
    }
  }, [patientId, appointmentId, wanted]);

  React.useEffect(() => { load({ initial: true }); }, [load]);

  // COSTS PARSINA CREDITS. The button on the دارو tab, and nothing else — the
  // interaction sweep beside it is a separate billed call on its own debounce,
  // and this must never ride along with it.
  const suggestions = useAiSuggestions(
    'useVisitMedications.suggest',
    React.useCallback(async () => {
      const data = await bffQuery(MEDICATION_SUGGESTIONS_QUERY, { patientId, appointmentId });
      return data?.medicationSuggestions || { items: [], followUpTimeline: null };
    }, [patientId, appointmentId]),
  );

  return {
    medications,
    loading,
    reload: () => load(),
    suggestions: suggestions.data,
    suggesting: suggestions.loading,
    suggestError: suggestions.error,
    suggest: suggestions.ask,
    clearSuggestions: suggestions.clear,
  };
}

/** دستورات — this visit's orders, and the catalogue they are named from. */
export function useVisitOrders({ patientId, appointmentId, clinicId }) {
  const [orders, setOrders] = React.useState([]);
  const [catalog, setCatalog] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const wanted = useGuardedLoad(appointmentId);

  const load = React.useCallback(async ({ initial } = {}) => {
    // `patientId` is the gate even though this read does not use it: it is null
    // until the VISIT HEADER resolves, and so is `clinicId`. Without it this
    // hook fires once with no clinic and again with one — two wasted requests
    // per visit open, and a catalogue briefly missing the clinic's own rows.
    // Every other hook here waits for the same signal; this one did not.
    if (!appointmentId || !patientId) {
      setLoading(false);
      return;
    }
    const asked = appointmentId;
    if (initial) setLoading(true);
    // The catalogue is reference data and the orders are the record. Fired
    // together, settled separately: a catalogue that fails to load leaves the
    // dentist able to READ their orders, which is more than half the tab.
    const [ordersRes, catalogRes] = await Promise.allSettled([
      bffQuery(VISIT_ORDERS_QUERY, { appointmentId: asked }),
      bffQuery(ORDER_CATALOG_QUERY, { orderType: null, clinicId: clinicId || null }),
    ]);
    if (wanted.current !== asked) return;
    if (ordersRes.status === 'fulfilled') {
      setOrders(ordersRes.value?.visitOrders || []);
      setError(null);
    } else if (initial) {
      console.error('[useVisitOrders] failed:', ordersRes.reason?.message);
      setError(ordersRes.reason);
    }
    if (catalogRes.status === 'fulfilled') setCatalog(catalogRes.value?.dentalOrderCatalog || []);
    if (initial) setLoading(false);
  }, [appointmentId, patientId, clinicId, wanted]);

  React.useEffect(() => { load({ initial: true }); }, [load]);

  const create = React.useCallback(async (input) => {
    setSaving(true);
    try {
      await bffMutation(CREATE_VISIT_ORDER, { patientId, appointmentId, ...input });
      await load();
      return null;
    } catch (err) {
      console.error('[useVisitOrders] create failed:', err?.message);
      return err;
    } finally {
      setSaving(false);
    }
  }, [patientId, appointmentId, load]);

  const setStatus = React.useCallback(async (orderId, transition) => {
    setSaving(true);
    try {
      await bffMutation(SET_ORDER_STATUS, { appointmentId, orderId, transition });
      await load();
      return null;
    } catch (err) {
      console.error('[useVisitOrders] transition failed:', err?.message);
      return err;
    } finally {
      setSaving(false);
    }
  }, [appointmentId, load]);

  // COSTS PARSINA CREDITS. Only ever called from the button.
  const suggestions = useAiSuggestions(
    'useVisitOrders.suggest',
    React.useCallback(async () => {
      const data = await bffQuery(VISIT_ORDER_SUGGESTIONS_QUERY, { patientId, appointmentId });
      return data?.visitOrderSuggestions || { items: [], rationale: null, droppedCount: 0 };
    }, [patientId, appointmentId]),
  );

  return {
    orders,
    catalog,
    loading,
    error,
    saving,
    create,
    setStatus,
    reload: () => load({ initial: true }),
    suggestions: suggestions.data,
    suggesting: suggestions.loading,
    suggestError: suggestions.error,
    suggest: suggestions.ask,
    clearSuggestions: suggestions.clear,
  };
}

/**
 * تشخیص — the recorded diagnoses, and the AI differential beside them.
 *
 * The two are DELIBERATELY separate pieces of state. `diagnoses` is the record
 * and loads with the tab; `differential` is a suggestion, COSTS CREDITS, and
 * loads only when `suggest()` is called. Merging them into one list would make
 * it impossible to tell what has been recorded from what has merely been
 * proposed — and would bill a credit every time the tab is opened.
 */
export function useVisitDiagnoses({ patientId, appointmentId }) {
  const [diagnoses, setDiagnoses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [differential, setDifferential] = React.useState(null);
  const [suggesting, setSuggesting] = React.useState(false);
  const [suggestError, setSuggestError] = React.useState(null);
  const wanted = useGuardedLoad(appointmentId);

  const load = React.useCallback(async ({ initial } = {}) => {
    if (!patientId || !appointmentId) {
      setLoading(false);
      return;
    }
    const asked = appointmentId;
    if (initial) setLoading(true);
    try {
      const data = await bffQuery(VISIT_DIAGNOSES_QUERY, { patientId, appointmentId: asked });
      if (wanted.current !== asked) return;
      setDiagnoses(data?.visitDiagnoses || []);
      setError(null);
    } catch (err) {
      if (wanted.current !== asked) return;
      console.error('[useVisitDiagnoses] failed:', err?.message);
      if (initial) setError(err);
    } finally {
      if (initial && wanted.current === asked) setLoading(false);
    }
  }, [patientId, appointmentId, wanted]);

  React.useEffect(() => { load({ initial: true }); }, [load]);

  /** COSTS PARSINA CREDITS. Only ever called from a button. */
  const suggest = React.useCallback(async () => {
    setSuggesting(true);
    setSuggestError(null);
    try {
      const data = await bffQuery(VISIT_DIFFERENTIAL_QUERY, { patientId, appointmentId });
      setDifferential(data?.visitDifferential || []);
      return null;
    } catch (err) {
      console.error('[useVisitDiagnoses] differential failed:', err?.message);
      // The credit case gets its own branch because "top up" is actionable and
      // "unavailable" is not — the same split نسخه makes for its sweep.
      setSuggestError(errorCode(err) === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable');
      return err;
    } finally {
      setSuggesting(false);
    }
  }, [patientId, appointmentId]);

  const record = React.useCallback(async (input) => {
    setSaving(true);
    try {
      await bffMutation(RECORD_DIAGNOSIS, { patientId, appointmentId, ...input });
      await load();
      return null;
    } catch (err) {
      console.error('[useVisitDiagnoses] record failed:', err?.message);
      return err;
    } finally {
      setSaving(false);
    }
  }, [patientId, appointmentId, load]);

  const remove = React.useCallback(async (diagnosisId) => {
    setSaving(true);
    try {
      await bffMutation(DELETE_DIAGNOSIS, { patientId, diagnosisId });
      await load();
      return null;
    } catch (err) {
      console.error('[useVisitDiagnoses] delete failed:', err?.message);
      return err;
    } finally {
      setSaving(false);
    }
  }, [patientId, load]);

  return {
    diagnoses, loading, error, saving, record, remove,
    differential, suggesting, suggestError, suggest,
    reload: () => load({ initial: true }),
  };
}

/**
 * پرسش‌ها — «دستیار بالینی».
 *
 * NOTHING LOADS BY ITSELF. Both calls cost credits, so the tab opens with an
 * explanation and a button rather than with a bill.
 */
export function useClinicalAssistant({ patientId, appointmentId }) {
  const [questions, setQuestions] = React.useState(null);
  const [loadingQuestions, setLoadingQuestions] = React.useState(false);
  const [questionsError, setQuestionsError] = React.useState(null);
  const [answers, setAnswers] = React.useState([]);
  const [asking, setAsking] = React.useState(false);
  const [askError, setAskError] = React.useState(null);

  /** COSTS PARSINA CREDITS. */
  const generate = React.useCallback(async ({ diagnoses = [] } = {}) => {
    setLoadingQuestions(true);
    setQuestionsError(null);
    try {
      const data = await bffQuery(VISIT_QUESTIONS_QUERY, {
        patientId,
        appointmentId,
        diagnosisCodes: diagnoses.map((d) => d.code).filter(Boolean),
        diagnosisNames: diagnoses.map((d) => d.display).filter(Boolean),
      });
      setQuestions(data?.visitQuestions || { items: [], fallback: false });
      return null;
    } catch (err) {
      console.error('[useClinicalAssistant] questions failed:', err?.message);
      setQuestionsError(errorCode(err) === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable');
      return err;
    } finally {
      setLoadingQuestions(false);
    }
  }, [patientId, appointmentId]);

  /** COSTS PARSINA CREDITS. */
  const ask = React.useCallback(async (question) => {
    setAsking(true);
    setAskError(null);
    try {
      const data = await bffMutation(ASK_VISIT_QUESTION, { patientId, appointmentId, question });
      const answer = data?.askVisitQuestion;
      if (answer) setAnswers((prev) => [...prev, { question, ...answer }]);
      return null;
    } catch (err) {
      console.error('[useClinicalAssistant] ask failed:', err?.message);
      setAskError(errorCode(err) === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable');
      return err;
    } finally {
      setAsking(false);
    }
  }, [patientId, appointmentId]);

  return {
    questions, loadingQuestions, questionsError, generate,
    answers, asking, askError, ask,
  };
}

/**
 * دستیار شرح حال — the doctor app's SOAP flow, on the تشخیص tab.
 *
 * One button, two calls, TWO error states: the refine (fill the note) and the
 * advice (summary + suggested diagnosis) fail independently, and a dentist
 * holding a good draft should not lose it because the advice call died. The
 * one shared failure is an empty wallet — when the FIRST call reports it, the
 * second is skipped rather than billed into the same refusal.
 *
 * NOTHING LOADS BY ITSELF, and nothing here writes: the draft is applied by
 * the pane through saveVisitNote, so the record only ever says what the
 * dentist chose.
 */
export function useSoapAssistant({ patientId, appointmentId }) {
  const [analyzing, setAnalyzing] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [advice, setAdvice] = React.useState(null);
  const [findings, setFindings] = React.useState(null);
  const [refineError, setRefineError] = React.useState(null);
  const [adviceError, setAdviceError] = React.useState(null);
  const [findingsError, setFindingsError] = React.useState(null);

  /** COSTS PARSINA CREDITS (both calls). Only ever fired from the button. */
  const analyze = React.useCallback(async (transcript) => {
    setAnalyzing(true);
    setRefineError(null);
    setAdviceError(null);
    setFindingsError(null);
    try {
      const data = await bffMutation(REFINE_VISIT_NOTE, { patientId, appointmentId, transcript });
      setDraft(data?.refineVisitNote || null);
    } catch (err) {
      console.error('[useSoapAssistant] refine failed:', err?.message);
      const kind = errorCode(err) === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable';
      setRefineError(kind);
      if (kind === 'credits') {
        // The other two calls bill too; running them now buys more refusals.
        setAdviceError('credits');
        setFindingsError('credits');
        setAnalyzing(false);
        return err;
      }
    }
    try {
      const data = await bffMutation(GENERATE_VISIT_ADVICE, { patientId, appointmentId, transcript });
      setAdvice(data?.generateVisitAdvice || null);
    } catch (err) {
      console.error('[useSoapAssistant] advice failed:', err?.message);
      setAdviceError(errorCode(err) === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable');
    }
    // The tooth read is LAST and its own try: it is the youngest of the three
    // and the one most likely to be unavailable, and the dentist should still
    // get the note draft and the advice when it is.
    try {
      const data = await bffMutation(EXTRACT_TOOTH_FINDINGS, {
        patientId, appointmentId, transcript,
      });
      setFindings(data?.extractToothFindings || null);
    } catch (err) {
      console.error('[useSoapAssistant] tooth findings failed:', err?.message);
      setFindingsError(errorCode(err) === 'INSUFFICIENT_CREDITS' ? 'credits' : 'unavailable');
    } finally {
      setAnalyzing(false);
    }
    return null;
  }, [patientId, appointmentId]);

  // Applied or dismissed, a draft leaves the screen the same way.
  const clearDraft = React.useCallback(() => setDraft(null), []);
  const clearAdvice = React.useCallback(() => setAdvice(null), []);
  const clearFindings = React.useCallback(() => setFindings(null), []);

  return {
    analyzing, draft, advice, findings, refineError, adviceError, findingsError,
    analyze, clearDraft, clearAdvice, clearFindings,
  };
}

/** فایل‌ها — the documents and their extracted reports. */
export function useVisitFiles({ patientId, appointmentId }) {
  const [files, setFiles] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [busyFileId, setBusyFileId] = React.useState(null);
  const wanted = useGuardedLoad(appointmentId);

  const load = React.useCallback(async ({ initial } = {}) => {
    if (!patientId || !appointmentId) {
      setLoading(false);
      return;
    }
    const asked = appointmentId;
    if (initial) setLoading(true);
    try {
      const data = await bffQuery(VISIT_FILES_QUERY, { patientId, appointmentId: asked });
      if (wanted.current !== asked) return;
      setFiles(data?.visitFiles || []);
      setError(null);
    } catch (err) {
      if (wanted.current !== asked) return;
      console.error('[useVisitFiles] failed:', err?.message);
      if (initial) setError(err);
    } finally {
      if (initial && wanted.current === asked) setLoading(false);
    }
  }, [patientId, appointmentId, wanted]);

  React.useEffect(() => { load({ initial: true }); }, [load]);

  const upload = React.useCallback(async (input) => {
    setUploading(true);
    try {
      await bffMutation(UPLOAD_VISIT_FILE, { patientId, appointmentId, ...input });
      await load();
      return null;
    } catch (err) {
      console.error('[useVisitFiles] upload failed:', err?.message);
      return err;
    } finally {
      setUploading(false);
    }
  }, [patientId, appointmentId, load]);

  const remove = React.useCallback(async (fileId) => {
    try {
      await bffMutation(DELETE_VISIT_FILE, { patientId, fileId });
      await load();
      return null;
    } catch (err) {
      console.error('[useVisitFiles] delete failed:', err?.message);
      return err;
    }
  }, [patientId, load]);

  // ── The three per-file actions ────────────────────────────────────────────
  //
  // `busyFileId` and not a boolean: the list shows every file at once, and a
  // single flag would spin every row's button while one of them works. It holds
  // the id so exactly the pressed control reports for duty.
  //
  // All three refetch the list rather than patching the row in place. A patch
  // would be one more place that has to know how a file row is assembled from
  // three services, and the list is small.

  const runOcr = React.useCallback(async (fileId, documentType) => {
    setBusyFileId(fileId);
    try {
      await bffMutation(RUN_FILE_OCR, {
        patientId, appointmentId, fileId, documentType: documentType || null,
      });
      await load();
      return null;
    } catch (err) {
      console.error('[useVisitFiles] ocr failed:', err?.message);
      return err;
    } finally {
      setBusyFileId(null);
    }
  }, [patientId, appointmentId, load]);

  /**
   * Label an already-stored file AS a dental image.
   *
   * Every file uploaded before the image workspace existed has no link, so it
   * can be viewed and nothing else — annotating and reading both need a view
   * type. This is the repair, and it is why the workspace opens on such a file
   * at all rather than refusing it.
   */
  const register = React.useCallback(async (fileId, viewType, teethFdi) => {
    setBusyFileId(fileId);
    try {
      await bffMutation(REGISTER_VISIT_IMAGE, {
        patientId, appointmentId, fileId, viewType, teethFdi: teethFdi || [],
      });
      await load();
      return null;
    } catch (err) {
      console.error('[useVisitFiles] register failed:', err?.message);
      return err;
    } finally {
      setBusyFileId(null);
    }
  }, [patientId, appointmentId, load]);

  const analyze = React.useCallback(async (fileId) => {
    setBusyFileId(fileId);
    try {
      const data = await bffMutation(ANALYZE_VISIT_IMAGE, {
        patientId, appointmentId, fileId,
      });
      await load();
      // Returned as well as reloaded: the caller wants to know whether the read
      // found anything and whether it was charged, and «هیچ یافته‌ای پیدا نشد»
      // is a different message from an error.
      return { result: data?.analyzeVisitImage || null, error: null };
    } catch (err) {
      console.error('[useVisitFiles] image read failed:', err?.message);
      return { result: null, error: err };
    } finally {
      setBusyFileId(null);
    }
  }, [patientId, appointmentId, load]);

  const saveAnnotations = React.useCallback(async (fileId, annotations) => {
    setBusyFileId(fileId);
    try {
      await bffMutation(SAVE_IMAGE_ANNOTATIONS, {
        patientId,
        appointmentId,
        fileId,
        // Only the fields the input type declares. Spreading the whole mark
        // would send `authorId` — which the server stamps and the schema does
        // not accept — and GraphQL refuses the entire operation for it.
        annotations: annotations.map((a) => ({
          id: a.id,
          kind: a.kind,
          x: a.x, y: a.y,
          w: a.w ?? null, h: a.h ?? null,
          x2: a.x2 ?? null, y2: a.y2 ?? null,
          points: a.points || [],
          toothFdi: a.toothFdi || null,
          comment: a.comment || '',
          color: a.color || null,
          createdAtMs: a.createdAtMs || null,
        })),
      });
      await load();
      return null;
    } catch (err) {
      console.error('[useVisitFiles] annotation save failed:', err?.message);
      return err;
    } finally {
      setBusyFileId(null);
    }
  }, [patientId, appointmentId, load]);

  return {
    files, loading, error, uploading, busyFileId,
    upload, remove, runOcr, analyze, saveAnnotations, register,
    reload: () => load({ initial: true }),
  };
}

export default useVisit;
